<template>
  <v-layout align-start>
    <v-flex>
      <v-toolbar flat color="transparent" class="mt-2">
        <v-toolbar-title>Cumbre 11D — Analítica</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-text-field
          v-model="desde"
          label="Desde"
          type="date"
          dense
          outlined
          hide-details
          style="max-width: 160px"
          class="mr-2"
        ></v-text-field>
        <v-text-field
          v-model="hasta"
          label="Hasta"
          type="date"
          dense
          outlined
          hide-details
          style="max-width: 160px"
          class="mr-2"
        ></v-text-field>
        <v-btn small text @click="load" :loading="loading">
          <v-icon small class="mr-1">refresh</v-icon> Actualizar
        </v-btn>
      </v-toolbar>

      <p class="grey--text body-2 mt-3 mb-4">
        Cuánta gente entró a Cumbre 11D (las dos landings, lla-site y el proyecto standalone) y qué
        hizo cada una — vistas, acciones del funnel y cada llamada real a los endpoints. Sin fechas
        elegidas, muestra los últimos 30 días.
      </p>

      <v-layout row wrap class="mb-2">
        <v-flex xs12 sm4 class="pa-2">
          <v-card class="pa-3 text-center">
            <div class="display-1 font-weight-bold">{{ resumen.visitas }}</div>
            <div class="caption grey--text">visitas (sesiones distintas)</div>
            <div class="caption grey--text mt-1">no cuántas veces se disparó un evento, sino cuánta gente entró</div>
          </v-card>
        </v-flex>
        <v-flex xs12 sm4 class="pa-2">
          <v-card class="pa-3 text-center">
            <div class="display-1 font-weight-bold">{{ totalEventos }}</div>
            <div class="caption grey--text">eventos del funnel</div>
            <div class="caption grey--text mt-1">visitas + acciones, sumadas</div>
          </v-card>
        </v-flex>
        <v-flex xs12 sm4 class="pa-2">
          <v-card class="pa-3 text-center">
            <div class="display-1 font-weight-bold" :class="{ 'red--text': totalErrores > 0 }">{{ totalErrores }}</div>
            <div class="caption grey--text">llamadas con error (4xx/5xx)</div>
            <div class="caption grey--text mt-1">de {{ totalLlamadasApi }} llamadas a los endpoints</div>
          </v-card>
        </v-flex>
      </v-layout>

      <v-layout row wrap>
        <v-flex xs12 md6 class="pa-2">
          <v-card>
            <v-card-title class="subtitle-1">Embudo</v-card-title>
            <v-card-text>
              <p class="caption grey--text">
                "Veces" es cuántas veces pasó el evento; "sesiones" es cuánta gente distinta llegó a
                ese paso — la que importa para leer dónde se cae la venta.
              </p>
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th>Evento</th>
                    <th class="text-right">Veces</th>
                    <th class="text-right">Sesiones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="e in resumen.funnel" :key="e.type + e.name">
                    <td>
                      {{ e.name }}
                      <v-chip x-small outlined class="ml-1">{{ e.type }}</v-chip>
                    </td>
                    <td class="text-right">{{ e.total }}</td>
                    <td class="text-right font-weight-bold">{{ e.sesiones }}</td>
                  </tr>
                  <tr v-if="!resumen.funnel.length">
                    <td colspan="3" class="text-center grey--text">Todavía no hay eventos en este rango</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card-text>
          </v-card>
        </v-flex>

        <v-flex xs12 md6 class="pa-2">
          <v-card>
            <v-card-title class="subtitle-1">Endpoints</v-card-title>
            <v-card-text>
              <p class="caption grey--text">
                Cada llamada real a cualquier ruta de Cumbre 11D, la haya hecho una persona o el
                propio sistema (recordatorios, cron). Muchos errores en un endpoint es la primera
                señal de que algo se rompió del lado del front.
              </p>
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th>Endpoint</th>
                    <th class="text-right">Llamadas</th>
                    <th class="text-right">Errores</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="e in resumen.endpoints" :key="e.name">
                    <td><code>{{ e.name }}</code></td>
                    <td class="text-right">{{ e.total }}</td>
                    <td class="text-right" :class="{ 'red--text font-weight-bold': e.errores > 0 }">{{ e.errores }}</td>
                  </tr>
                  <tr v-if="!resumen.endpoints.length">
                    <td colspan="3" class="text-center grey--text">Todavía no hay llamadas en este rango</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card-text>
          </v-card>
        </v-flex>

        <v-flex xs12 class="pa-2">
          <v-card>
            <v-card-title class="subtitle-1">Actividad reciente</v-card-title>
            <v-card-text>
              <p class="caption grey--text">Las últimas {{ resumen.recientes.length }} filas, sin agregar nada.</p>
              <v-data-table
                :headers="headersRecientes"
                :items="resumen.recientes"
                :items-per-page="15"
                dense
                class="elevation-0"
                no-data-text="Todavía no hay actividad"
              >
                <template v-slot:item.createdAt="{ item }">
                  {{ fecha(item.createdAt) }}
                </template>
                <template v-slot:item.type="{ item }">
                  <v-chip x-small outlined>{{ item.type }}</v-chip>
                </template>
                <template v-slot:item.params="{ item }">
                  <span class="caption grey--text">{{ resumenParams(item) }}</span>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>
    </v-flex>
  </v-layout>
</template>

<script>
import axios from "axios";

/**
 * Analítica de Cumbre 11D (2026-09-18, a pedido explícito): "un contador por cada vez que se
 * ingrese al sistema, y las acciones que hace el usuario también... y todos sus endpoints".
 *
 * Ninguna cuenta se hace acá: todo viene de `GET /cumbre11d/analytics/summary`, mismo criterio
 * que el tablero (`cumbre11dDashboard`) — sumar del lado del navegador obligaría a bajarse todos
 * los eventos para mostrar tres números.
 */
export default {
  data() {
    return {
      loading: false,
      desde: null,
      hasta: null,
      resumen: this.vacio(),
      headersRecientes: [
        { text: "Cuándo", value: "createdAt" },
        { text: "Tipo", value: "type" },
        { text: "Evento", value: "name" },
        { text: "Sesión", value: "sessionId" },
        { text: "Origen", value: "source" },
        { text: "Detalle", value: "params" },
      ],
    };
  },
  computed: {
    totalEventos() {
      return this.resumen.funnel.reduce((acc, e) => acc + Number(e.total || 0), 0);
    },
    totalLlamadasApi() {
      return this.resumen.endpoints.reduce((acc, e) => acc + Number(e.total || 0), 0);
    },
    totalErrores() {
      return this.resumen.endpoints.reduce((acc, e) => acc + Number(e.errores || 0), 0);
    },
  },
  async created() {
    await this.load();
  },
  methods: {
    // La forma vacía existe para que la pantalla se pinte antes de que llegue la respuesta.
    vacio() {
      return { visitas: 0, funnel: [], endpoints: [], recientes: [] };
    },

    async load() {
      this.loading = true;
      try {
        const params = {};
        if (this.desde) params.desde = this.desde;
        if (this.hasta) params.hasta = this.hasta;
        const { data } = await axios.get("cumbre11d/analytics/summary", {
          params,
          headers: { Authorization: localStorage.getItem("token") },
        });
        this.resumen = data;
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },

    fecha(iso) {
      return new Date(iso).toLocaleString("es-UY", { timeZone: "America/Montevideo" });
    },
    // Nunca la cédula/email/nombre de nadie: `params` acá adentro es lo mismo que ya manda
    // `track()` en la landing (§19 de la spec), que ya excluye datos personales a propósito.
    resumenParams(item) {
      if (!item.params) return "—";
      return Object.entries(item.params)
        .filter(([, v]) => v !== undefined && v !== null && v !== "")
        .map(([k, v]) => `${k}=${v}`)
        .join(" · ");
    },
  },
};
</script>
